import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { QueryProvider } from './contexts/QueryContext';
import { ProtectedRoute } from './components/Auth/ProtectedRoute';
import { Layout } from './components/Layout/Layout';
import { Dashboard } from './components/Dashboard/Dashboard';
import { UserProfile } from './components/Profile/UserProfile';
import { ChangePassword } from './components/Profile/ChangePassword';
import { UserManagement } from './components/Users/UserManagement';
import { AccountMatching } from './components/AccountMatching/AccountMatching';
import { ClientManagement } from './components/Clients/ClientManagement';
import { AccountManagement } from './components/Accounts/AccountManagement';
import { EmployeeManagement } from './components/Employees/EmployeeManagement';
import BlotterManagement from './components/Blotter/BlotterManagement';
import BlotterDetail from './components/Blotter/BlotterDetail';
import { TransactionList } from './components/Transaction';
import { ComingSoon } from './components/Common/ComingSoon';
import BlotterReport from './components/Reports/BlotterReport';
import ManualChecks from './components/ManualChecks/ManualChecks';
import UserReport from './components/Reports/UserReport';
import ClientReport from './components/Reports/ClientReport';
import AccountReport from './components/Reports/AccountReport';
import CheckReport from './components/Reports/CheckReport';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import './App.css';

const AppRoutes: React.FC = () => {
  useDocumentTitle();

  return (
    <Routes>
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* Profile Routes */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Layout>
                  <UserProfile />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <Layout>
                  <ChangePassword />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* User Management Routes */}
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <Layout>
                  <UserManagement />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Data Management Routes */}
          <Route
            path="/csv-upload"
            element={
              <ProtectedRoute>
                <Layout>
                  <BlotterManagement />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/blotters"
            element={
              <ProtectedRoute>
                <Layout>
                  <BlotterManagement />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/blotters/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <BlotterDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/csv-history"
            element={<Navigate to="/csv-upload" replace />}
          />
          <Route
            path="/transactions"
            element={
              <ProtectedRoute>
                <Layout>
                  <TransactionList />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manual-checks"
            element={
              <ProtectedRoute>
                <Layout>
                  <ManualChecks />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Account Management Routes */}
          <Route
            path="/accounts"
            element={
              <ProtectedRoute>
                <Layout>
                  <AccountManagement />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <ProtectedRoute>
                <Layout>
                  <ClientManagement />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees"
            element={
              <ProtectedRoute>
                <Layout>
                  <EmployeeManagement />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Configuration Routes */}
          <Route
            path="/account-matching"
            element={
              <ProtectedRoute>
                <Layout>
                  <AccountMatching />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route 
            path="/commission-presets" 
            element={
              <ProtectedRoute>
                <Layout>
                  <ComingSoon 
                    title="Commission Presets" 
                    description="Manage commission rate presets for quick assignment."
                    features={[
                      "Preset templates",
                      "Rate calculations",
                      "Popular presets",
                      "Custom percentages"
                    ]}
                  />
                </Layout>
              </ProtectedRoute>
            } 
          />

          {/* Reports Routes */}
          <Route
            path="/reports/blotter"
            element={
              <ProtectedRoute>
                <Layout>
                  <BlotterReport />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/user"
            element={
              <ProtectedRoute>
                <Layout>
                  <UserReport />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/client"
            element={
              <ProtectedRoute>
                <Layout>
                  <ClientReport />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/account"
            element={
              <ProtectedRoute>
                <Layout>
                  <AccountReport />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* R17 Task H (Peter #2a): Check Report by Firm */}
          <Route
            path="/reports/checks"
            element={
              <ProtectedRoute>
                <Layout>
                  <CheckReport />
                </Layout>
              </ProtectedRoute>
            }
          />


          {/* Catch all route */}
          <Route 
            path="*" 
            element={
              <ProtectedRoute>
                <Layout>
                  <ComingSoon 
                    title="Page Not Found" 
                    description="The page you're looking for doesn't exist."
                  />
                </Layout>
              </ProtectedRoute>
            } 
          />
    </Routes>
  );
};

function App() {
  return (
    <QueryProvider>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </QueryProvider>
  );
}

export default App;
